import { useEffect, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import {
  ChevronLeft,
  Contact,
  Copy,
  Key,
  Loader2,
  Pencil,
  Save,
  Trash2,
  UserCheck,
  UserPlus,
  UserX,
  X,
} from "lucide-react";

import { Input } from "@components/ui/Input";

import type { Entitlement } from "@pages/ProductCatalog/store";

import { Loading } from "./components/layout/Loading";
import { AdminNotes, NoticeOnFileBadges } from "./components/notes/AdminNotes";
import { Table } from "./components/ui/table/Table";
import { listEntitlements } from "./queries/entitlements/entitlementApi";
import { apiIgnoredCustomerFilter } from "./queries/transactions/customerFilter";
import {
  formatAmount,
  formatNullableDate,
  isLegacyRow,
  LEGACY_BADGE_CLASS,
  statusBadgeClass,
  transactionDetailPath,
} from "./queries/transactions/format";
import { searchTransactions } from "./queries/transactions/transactionQueries";
import { emailToSend, NO_AUTOFILL_PROPS } from "./queries/users/emailEdit";
import { type User as UserType, UserStatus } from "./queries/users/types";
import {
  type AdminUserDetail,
  type AdminUserNote,
  type AdminUserNotice,
  type AdminUserVideoPreferences,
  createAdminUser,
  deactivateAdminUser,
  deleteAdminUser,
  generateAdminPassword,
  getAdminUser,
  reactivateAdminUser,
  setAdminUserPassword,
  updateAdminUser,
  type UpdateAdminUserBody,
} from "./queries/users/userApi";

import styles from "./Users.module.css";

export interface UserFormFields {
  email: string;
  entitlements: Entitlement[];
}

// Simple confirmation dialog component
const ConfirmDialog = ({
  isOpen,
  title,
  message,
  onConfirm,
  onCancel,
  confirmText = "Delete",
  cancelText = "Cancel",
  isLoading = false,
}: {
  isOpen: boolean;
  title: string;
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
  confirmText?: string;
  cancelText?: string;
  isLoading?: boolean;
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md mx-4 shadow-xl">
        <h3 className="text-lg font-semibold mb-2">{title}</h3>
        <p className="text-gray-600 mb-6">{message}</p>
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={isLoading}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            {cancelText}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isLoading}
            className="flex items-center px-4 py-2 rounded-md text-white bg-red-600 hover:bg-red-700 transition-colors disabled:opacity-50"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              confirmText
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export const User = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const isCreating = userId === "new";

  const [user, setUser] = useState<UserType>();
  const [entitlements, setEntitlements] = useState<Entitlement[]>([]);

  const [editing, setEditing] = useState(isCreating);
  // The email is only editable (and only rendered as an input) after an
  // explicit "Change email" click, separate from the page's edit mode, so
  // browser/password-manager autofill can never change it (emailEdit.ts).
  const [editingEmail, setEditingEmail] = useState(false);
  const [isLoading, setIsLoading] = useState(!isCreating);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Delete confirmation dialog state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // New password state
  const [password, setPassword] = useState("");
  const [isGeneratingPassword, setIsGeneratingPassword] = useState(false);
  const [passwordCopied, setPasswordCopied] = useState(false);
  // Status change confirmation dialog state
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [isChangingStatus, setIsChangingStatus] = useState(false);

  // Video preferences — admin can flip explicitEligible and explicitContent.
  // explicitOverlayShown stays internal-only. Loaded as part of the user detail
  // (GET /admin/users/{id}) and saved back through the same PATCH, so there's
  // no separate preferences request.
  const [prefs, setPrefs] = useState<AdminUserVideoPreferences>();

  // Customer notices on file (OMG-2558), loaded with the user detail so the
  // header flag can never be hidden by a separate request failing. null = not
  // loaded yet; undefined = the API didn't report them (predates OMG-2558).
  const [notices, setNotices] = useState<AdminUserNotice[] | undefined | null>(
    null,
  );
  const handleNoteCreated = (note: AdminUserNote) => {
    if (!note.notice) return;
    const recorded: AdminUserNotice = {
      noteId: note.id,
      type: note.notice.type,
      receivedOn: note.notice.receivedOn,
      recordedAt: note.createdAt,
    };
    // Same order as the API: most recently received first.
    setNotices((prev) =>
      [recorded, ...(prev ?? [])].sort(
        (a, b) =>
          b.receivedOn.localeCompare(a.receivedOn) || b.noteId - a.noteId,
      ),
    );
  };
  const [draftEligible, setDraftEligible] = useState(false);
  const [draftContent, setDraftContent] = useState(false);
  // Sync drafts to the server value whenever we're outside edit mode. Covers
  // initial load, post-save reset, and Cancel (where prefs didn't change but
  // drafts may diverge from server state).
  useEffect(() => {
    if (editing) return;
    if (!prefs) return;
    setDraftEligible(prefs.explicitEligible);
    setDraftContent(prefs.explicitContent);
  }, [editing, prefs?.explicitEligible, prefs?.explicitContent]);
  const isPrefsDirty =
    !!prefs &&
    (prefs.explicitEligible !== draftEligible ||
      prefs.explicitContent !== draftContent);

  // New-stack transactions (omg-api pending_settlement), keyed on the
  // customer id rather than email so they survive email changes (OMG-2229).
  // customer.id is the Cognito sub; fall back to the User row id, which
  // equals the sub for checkout-created accounts.
  const {
    data: txnData,
    isLoading: txnLoading,
    isError: txnError,
  } = useQuery({
    queryKey: ["admin-user-transactions", user?.id] as const,
    queryFn: () =>
      searchTransactions({
        customerId: user?.id as string,
        limit: 100,
        offset: 0,
        sortOrder: "desc",
      }),
    enabled: !!user?.id && !isCreating,
    retry: false,
  });

  // Deploy-order guard — see apiIgnoredCustomerFilter. An omg-api that predates
  // the `customerId` filter returns the whole table, which this section would
  // otherwise render as one member's transactions.
  const txnRows = txnData?.transactions ?? [];
  const txnFilterIgnored = apiIgnoredCustomerFilter(txnRows, user?.id);

  // Use form with validation
  const {
    handleSubmit,
    register,
    reset,
    formState: { errors },
    getValues,
    setValue,
    control,
  } = useForm<UserFormFields>({
    defaultValues: {
      email: "",
      entitlements: [],
    },
  });

  const { append: appendEntitlement, remove: removeEntitlement } =
    useFieldArray({
      name: "entitlements",
      control,
    });

  const loadEntitlements = async () => {
    try {
      setEntitlements(await listEntitlements());
    } catch (err) {
      console.error("Error fetching entitlements:", err);
    }
  };

  const getUser = async () => {
    if (!userId) return;
    setIsLoading(true);
    setError("");
    setEditingEmail(false);

    try {
      const detail = await getAdminUser(userId);
      const loadedUser: UserType = {
        id: detail.id,
        email: detail.email,
        status: detail.status,
        createdAt: detail.createdAt,
        entitlements: detail.entitlements.map((name) => ({ name })),
      };

      setUser(loadedUser);
      setPrefs(detail.preferences);
      setNotices(detail.notices);
      reset({
        email: loadedUser.email,
        entitlements: loadedUser.entitlements,
      });
    } catch (err) {
      setError("Failed to load user data. Please try again.");
      console.error("Error fetching user:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!userId) return;
    setIsDeleting(true);
    setError("");
    setSuccess("");

    try {
      await deleteAdminUser(userId);
      setSuccess("User successfully deleted from both Cognito and database.");
      setShowDeleteDialog(false);
      setTimeout(() => navigate("..", { replace: true }), 2000);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to delete user. Please try again.",
      );
      console.error("Error deleting user:", err);
      setShowDeleteDialog(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleGeneratePassword = async () => {
    if (!userId || isCreating) {
      setError(
        "Cannot generate password for new user. Please save the user first.",
      );
      return;
    }

    setIsGeneratingPassword(true);
    setError("");

    try {
      // The omg-api set-password endpoint takes an explicit password (it does
      // not generate one), so build a strong random password here, set it, then
      // surface it for the admin to copy.
      const newPassword = generateAdminPassword();
      await setAdminUserPassword(userId, newPassword);
      setPassword(newPassword);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to generate new password. Please try again.",
      );
      console.error("Error generating password:", err);
    } finally {
      setIsGeneratingPassword(false);
    }
  };

  const handleCopyPassword = async () => {
    if (!password) return;

    try {
      await navigator.clipboard.writeText(password);
      setPasswordCopied(true);

      // Reset the copied state after 2 seconds
      setTimeout(() => {
        setPasswordCopied(false);
      }, 2000);
    } catch (err) {
      console.error("Failed to copy password:", err);
      setError("Failed to copy password to clipboard.");
    }
  };

  const handleStatusChange = async () => {
    if (!userId) return;
    setIsChangingStatus(true);
    setError("");

    const isActive = user?.status !== UserStatus.DEACTIVATED;

    try {
      const detail = isActive
        ? await deactivateAdminUser(userId)
        : await reactivateAdminUser(userId);

      setUser((prev) => (prev ? { ...prev, status: detail.status } : prev));
      setEditing(false);
      setSaveSuccess(true);

      setTimeout(() => {
        setSaveSuccess(false);
        setEditing(false);
      }, 3000);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : `Failed to ${isActive ? "deactivate" : "reactivate"} user. Please try again.`,
      );
      console.error(
        `Error ${isActive ? "deactivating" : "reactivating"} user:`,
        err,
      );
    } finally {
      setIsChangingStatus(false);
      setShowStatusDialog(false);
    }
  };

  useEffect(() => {
    loadEntitlements();
  }, []);

  useEffect(() => {
    if (userId && !isCreating) {
      getUser();
    }
  }, [userId, isCreating]);

  // Handle checkbox change for entitlements
  const handleEntitlementChange = (
    entitlement: Entitlement,
    checked: boolean,
  ) => {
    const currentEntitlements = getValues("entitlements") || [];

    if (checked) {
      if (!currentEntitlements.some((e) => e.name === entitlement.name)) {
        appendEntitlement(entitlement);
      }
    } else {
      const index = currentEntitlements.findIndex(
        (e) => e.name === entitlement.name,
      );
      if (index >= 0) {
        removeEntitlement(index);
      }
    }
  };

  const isEntitlementSelected = (entitlementId: string) => {
    const currentEntitlements = getValues("entitlements") || [];
    return currentEntitlements.some((e) => e.name === entitlementId);
  };

  const onSubmit = async (formData: UserFormFields) => {
    setIsSaving(true);
    setSaveSuccess(false);
    setError("");

    if (!formData) {
      setError("Form data is missing");
      setIsSaving(false);
      return;
    }

    // Clean and validate the data
    const email = formData.email?.trim().toLowerCase();
    const entitlementNames = (formData.entitlements || []).map((e) => e.name);

    if (!email) {
      setError("Email is required");
      setIsSaving(false);
      return;
    }

    try {
      // omg-api returns the full detail (entitlements + preferences) on both
      // create and update; map it into local state the same way getUser does.
      const applyDetail = (detail: AdminUserDetail) => {
        const nextUser: UserType = {
          id: detail.id,
          email: detail.email,
          status: detail.status,
          createdAt: detail.createdAt,
          entitlements: detail.entitlements.map((name) => ({ name })),
        };
        setUser(nextUser);
        setPrefs(detail.preferences);
        setNotices(detail.notices);
        setEditing(false);
        setEditingEmail(false);
        setSaveSuccess(true);
        reset({
          email: nextUser.email,
          entitlements: nextUser.entitlements,
        });
        setTimeout(() => setSaveSuccess(false), 3000);
        return nextUser;
      };

      if (isCreating) {
        // Create requires a password (Cognito). Seed a strong random one; the
        // new user gets access via forgot-password, matching the legacy flow
        // where admin-create never set an admin-visible password.
        const created = await createAdminUser({
          email,
          password: generateAdminPassword(),
          ...(entitlementNames.length && { entitlements: entitlementNames }),
        });
        const createdUser = applyDetail(created);
        navigate(`/users/${createdUser.id}`, { replace: true });
      } else if (userId) {
        // Entitlement set, a deliberately changed email, and (if dirty) video
        // preferences all go in a single PATCH — one backend, applied
        // atomically. The email is omitted unless the admin used Change email
        // and typed a different address (emailEdit.ts).
        const nextEmail = emailToSend({
          editingEmail,
          draftEmail: email,
          loadedEmail: user?.email,
        });
        const body: UpdateAdminUserBody = {
          ...(nextEmail && { email: nextEmail }),
          entitlements: entitlementNames,
          ...(isPrefsDirty && {
            preferences: {
              explicitEligible: draftEligible,
              explicitContent: draftContent,
            },
          }),
        };
        const updated = await updateAdminUser(userId, body);
        applyDetail(updated);
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : `Failed to ${isCreating ? "create" : "update"} user. Please try again.`,
      );
      console.error(`Error ${isCreating ? "creating" : "updating"} user:`, err);
      console.error("Error details:", err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (isCreating) {
      // Navigate back to user list if creating
      window.history.back();
    } else {
      setEditing(false);
      setEditingEmail(false);
      // Reset form to the loaded user data.
      if (user) {
        reset({
          email: user.email,
          entitlements: user.entitlements ?? [],
        });
      }
      // Video preferences drafts are re-synced from `prefs` by the useEffect
      // above when `editing` flips back to false.
    }
  };

  const isUserActive = user?.status?.toLowerCase() === "active";
  const statusAction = isUserActive ? "deactivate" : "reactivate";
  const StatusIcon = isUserActive ? UserX : UserCheck;

  return (
    <div className="flex flex-col p-6 mx-auto">
      <Link to=".." className="mb-4">
        <ChevronLeft className="w-8 h-8" />
      </Link>

      <div className={styles.userForm}>
        <h1 className="flex items-center justify-between gap-2 text-xl font-semibold border-b pb-3">
          <span className="flex items-center gap-2">
            {isCreating ? (
              <>
                <UserPlus className="w-5 h-5" /> Create New User
              </>
            ) : (
              <>
                <Contact className="w-5 h-5" /> User Details
                <div className="flex flex-wrap items-center gap-2">
                  <div className="text-sm px-1 text-white bg-gray-500">
                    {user?.status}
                  </div>
                  {notices !== null && <NoticeOnFileBadges notices={notices} />}
                  {editing && user?.status !== UserStatus.ARCHIVED && (
                    <button
                      disabled={isSaving || isChangingStatus}
                      onClick={() => setShowStatusDialog(true)}
                      className={`flex items-center gap-1 px-2 py-1 text-xs rounded transition-colors ${
                        isUserActive
                          ? "bg-orange-100 text-orange-700 hover:bg-orange-200"
                          : "bg-green-100 text-green-700 hover:bg-green-200"
                      } disabled:opacity-50`}
                      title={`${statusAction} user`}
                    >
                      <StatusIcon className="w-3 h-3" />
                      {statusAction} user
                    </button>
                  )}
                </div>
              </>
            )}
          </span>
          {!isCreating && (
            <div className="flex items-center gap-2">
              <button
                disabled={isSaving || isDeleting || isChangingStatus}
                onClick={() => setShowDeleteDialog(true)}
                className="p-2 bg-transparent text-red-600 hover:bg-red-50 disabled:opacity-50"
                title="Delete User"
              >
                <Trash2 className="w-5 h-5" />
              </button>
              <button
                disabled={isSaving || isDeleting || isChangingStatus}
                // Closing edit mode is a cancel: it also drops any email draft.
                onClick={() => (editing ? handleCancel() : setEditing(true))}
                className="p-2 bg-transparent text-slate-900 hover:bg-gray-50 disabled:opacity-50"
              >
                {editing ? <X /> : <Pencil className="w-5 h-5" />}
              </button>
            </div>
          )}
        </h1>

        {isLoading ? (
          <div className="flex justify-center items-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-gray-500" />
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        ) : success ? (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded mb-4">
            {success}
          </div>
        ) : (
          <div className={"flex flex-col gap-2"}>
            {!isCreating && (
              <>
                <div className={"flex flex-col gap-0"}>
                  <h3>Id</h3>
                  <span className="text-xs">{user?.id}</span>
                </div>
                <div className={"flex flex-col gap-0"}>
                  <h3>Created At</h3>
                  <span className="text-xs">
                    {user?.createdAt &&
                      format(new Date(user.createdAt), "PPpp")}
                  </span>
                </div>
              </>
            )}

            <div className="mt-6">
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div
                  className={`${styles.formField}${errors.email ? "error" : ""}`}
                >
                  <label htmlFor="email">Email</label>
                  {isCreating || editingEmail ? (
                    <>
                      <Input
                        id="email"
                        type="email"
                        {...NO_AUTOFILL_PROPS}
                        autoFocus={editingEmail}
                        {...register("email", {
                          required: "Email is required",
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: "Invalid email address",
                          },
                        })}
                      />
                      {editingEmail && (
                        <button
                          type="button"
                          onClick={() => {
                            setEditingEmail(false);
                            if (user) setValue("email", user.email);
                          }}
                          className="self-start px-2 py-1 text-xs border border-gray-300 rounded text-gray-700 bg-white hover:bg-gray-50"
                        >
                          Keep current email
                        </button>
                      )}
                    </>
                  ) : (
                    // Plain text, not a read-only input: there is nothing for
                    // a browser or password manager to fill.
                    <div className="flex items-center gap-2">
                      <span id="email">{user?.email}</span>
                      {editing && (
                        <button
                          type="button"
                          onClick={() => setEditingEmail(true)}
                          className="px-2 py-1 text-xs border border-gray-300 rounded text-gray-700 bg-white hover:bg-gray-50"
                        >
                          Change email
                        </button>
                      )}
                    </div>
                  )}
                  {errors.email && <p>{errors.email.message}</p>}
                </div>
                {!isCreating && (
                  <>
                    <div className={"flex flex-col gap-0"}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {!password && (
                            <button
                              type="button"
                              onClick={handleGeneratePassword}
                              disabled={isGeneratingPassword}
                              className="flex items-center px-2 py-1 text-xs border border-gray-300 rounded text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50"
                              title="Generate New Password"
                            >
                              {isGeneratingPassword ? (
                                <>
                                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                  Generating...
                                </>
                              ) : (
                                <>
                                  <Key className="w-3 h-3 mr-1" />
                                  Generate New Password
                                </>
                              )}
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-2 mt-1">
                        {password && (
                          <>
                            <span className="text-xs font-mono bg-gray-50 px-2 py-1 rounded border">
                              {password}
                            </span>
                            <button
                              type="button"
                              onClick={handleCopyPassword}
                              className="flex items-center px-2 py-1 text-xs border border-gray-300 rounded text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                              title={
                                passwordCopied ? "Copied!" : "Copy Password"
                              }
                            >
                              <Copy
                                className={`w-3 h-3 ${passwordCopied ? "text-green-600" : ""}`}
                              />
                              {passwordCopied ? "Copied!" : "Copy"}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </>
                )}

                <div className={styles.formField}>
                  <h3>Entitlements</h3>
                  {entitlements.length === 0 ? (
                    <p className="text-gray-500">No entitlements available</p>
                  ) : (
                    <ul className="space-y-2">
                      {entitlements.map((entitlement) => (
                        <li
                          key={entitlement.name}
                          className="flex items-center"
                        >
                          <input
                            type="checkbox"
                            id={`entitlement-${entitlement.name}`}
                            checked={isEntitlementSelected(entitlement.name)}
                            onChange={(e) =>
                              handleEntitlementChange(
                                entitlement,
                                e.target.checked,
                              )
                            }
                            disabled={!editing}
                            className="mr-2 h-4 w-4 rounded border-gray-300"
                          />
                          <label
                            htmlFor={`entitlement-${entitlement.name}`}
                            className="text-sm font-medium text-gray-700"
                          >
                            {entitlement.name}
                          </label>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {!isCreating && (
                  <div className={styles.formField}>
                    <h3>Transactions</h3>
                    {txnError ? (
                      <p className="text-red-600">
                        Couldn't load transactions. Reload the page to try
                        again.
                      </p>
                    ) : txnFilterIgnored ? (
                      <p className="text-red-600">
                        Can't show transactions: the API returned rows for other
                        customers, which means it doesn't support filtering by
                        customer yet. This page needs omg-api with the{" "}
                        <code>customerId</code> filter deployed (OMG-2229). Use
                        the Transactions tab in the meantime.
                      </p>
                    ) : txnLoading ? (
                      <Loading />
                    ) : (
                      <Table>
                        <thead>
                          <tr>
                            <th>Transaction ID</th>
                            <th>Email</th>
                            <th>Status</th>
                            <th>Amount</th>
                            <th>Authorized</th>
                          </tr>
                        </thead>
                        <tbody>
                          {txnRows.length ? (
                            txnRows.map((txn) => (
                              <tr key={`${txn.source}-${txn.transactionId}`}>
                                <td>
                                  {/* Legacy rows (OMG-2574) open the read-only
                                      legacy detail; live rows the pending_settlement one. */}
                                  <Link
                                    to={transactionDetailPath(txn)}
                                    className="text-blue-600 hover:underline"
                                  >
                                    {txn.transactionId}
                                  </Link>
                                  {isLegacyRow(txn) && (
                                    <span className={LEGACY_BADGE_CLASS}>
                                      legacy
                                    </span>
                                  )}
                                </td>
                                <td>{txn.email}</td>
                                <td>
                                  <span
                                    className={statusBadgeClass(txn.status)}
                                  >
                                    {txn.status}
                                  </span>
                                </td>
                                <td>
                                  {formatAmount(txn.amount, txn.currency)}
                                </td>
                                <td>
                                  {formatNullableDate(
                                    txn.authorizedAt,
                                    (date) => format(date, "PPpp"),
                                  )}
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={5}>
                                <span className="text-gray-500">
                                  No transactions found.
                                </span>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    )}
                    {!txnFilterIgnored &&
                      !!txnData &&
                      txnData.total > txnRows.length && (
                        <p className="text-gray-500 text-xs mt-1">
                          Showing the {txnRows.length} most recent of{" "}
                          {txnData.total} transactions.
                        </p>
                      )}
                  </div>
                )}

                <div className={styles.formField}>
                  <h3>Video preferences</h3>
                  {!user?.id && (
                    <p className="text-gray-500">
                      Save the user to manage preferences.
                    </p>
                  )}
                  {user?.id && !prefs && <Loading />}
                  {user?.id && prefs && (
                    <div className="space-y-2">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={draftEligible}
                          onChange={(e) => setDraftEligible(e.target.checked)}
                          disabled={!editing || isSaving}
                          className="mr-2 h-4 w-4 rounded border-gray-300"
                        />
                        <span className="text-sm font-medium text-gray-700">
                          Explicit eligible (can toggle on/off in the app)
                        </span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={draftContent}
                          onChange={(e) => setDraftContent(e.target.checked)}
                          disabled={!editing || isSaving}
                          className="mr-2 h-4 w-4 rounded border-gray-300"
                        />
                        <span className="text-sm font-medium text-gray-700">
                          Explicit content enabled
                        </span>
                      </label>
                    </div>
                  )}
                </div>

                {saveSuccess && (
                  <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
                    User {isCreating ? "created" : "updated"} successfully!
                  </div>
                )}

                {editing && (
                  <div className="flex justify-end pt-4 gap-4">
                    <button
                      type="button"
                      disabled={isSaving}
                      onClick={handleCancel}
                      className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSaving}
                      className={`flex items-center px-4 py-2 ${isSaving ? "opacity-70 cursor-not-allowed" : ""}`}
                    >
                      {isSaving ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          {isCreating ? "Creating..." : "Saving..."}
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          {isCreating ? "Create User" : "Save Changes"}
                        </>
                      )}
                    </button>
                  </div>
                )}
              </form>
            </div>

            {/* Outside the edit form on purpose: notes save on their own, are
                never part of the PATCH, and a nested <form> is invalid HTML. */}
            {!isCreating && user?.id && (
              <AdminNotes userId={user.id} onNoteCreated={handleNoteCreated} />
            )}
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        title="Delete User"
        message={`Are you sure you want to delete this user${user?.email ? ` (${user.email})` : ""}? This action cannot be undone.${
          notices?.length
            ? ` This user has ${notices.length === 1 ? "a customer notice" : `${notices.length} customer notices`} on file; their admin notes are kept after deletion.`
            : ""
        }`}
        onConfirm={handleDeleteUser}
        onCancel={() => setShowDeleteDialog(false)}
        confirmText="Delete User"
        cancelText="Cancel"
        isLoading={isDeleting}
      />

      {/* Status Change Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showStatusDialog}
        title={`${isUserActive ? "Deactivate" : "Reactivate"} User`}
        message={`Are you sure you want to ${statusAction} this user${user?.email ? ` (${user.email})` : ""}?`}
        onConfirm={handleStatusChange}
        onCancel={() => setShowStatusDialog(false)}
        confirmText={`${isUserActive ? "Deactivate" : "Reactivate"} User`}
        cancelText="Cancel"
        isLoading={isChangingStatus}
      />
    </div>
  );
};
